export const CAMPAIGN_STATUS_GROUPS = [
  { value: "current", label: "Текущие", statuses: ["active", "paused"] },
  { value: "drafts", label: "Черновики", statuses: ["draft"] },
  {
    value: "finished",
    label: "Завершённые и отменённые",
    statuses: ["done", "cancelled", "stopped"],
  },
  { value: "all", label: "Все кампании", statuses: [] },
] as const;

export type CampaignStatusGroup =
  (typeof CAMPAIGN_STATUS_GROUPS)[number]["value"];

export function campaignStatusGroup(value: string | null): CampaignStatusGroup {
  return CAMPAIGN_STATUS_GROUPS.some((group) => group.value === value)
    ? (value as CampaignStatusGroup)
    : "current";
}

export function campaignListFilters(
  params: URLSearchParams,
  defaultCompanyId = "",
) {
  return {
    statusGroup: campaignStatusGroup(params.get("statusGroup")),
    companyId: params.get("companyId") ?? defaultCompanyId,
    tenantId: params.get("tenantId") ?? "",
  };
}

export function updateCampaignListFilters(
  params: URLSearchParams,
  patch: Partial<{
    statusGroup: CampaignStatusGroup;
    companyId: string;
    tenantId: string;
  }>,
): URLSearchParams {
  const result = new URLSearchParams(params);
  Object.entries(patch).forEach(([key, value]) => result.set(key, value));
  if ("companyId" in patch && !("tenantId" in patch)) result.delete("tenantId");
  return result;
}

/** Только внутренние страницы списка. Состояние перехода нельзя использовать для произвольного URL. */
export function campaignListReturnTo(
  state: unknown,
  fallback = "/campaigns",
): string {
  const value =
    state && typeof state === "object" && "campaignListUrl" in state
      ? state.campaignListUrl
      : undefined;
  return typeof value === "string" &&
    /^\/(campaigns|push)(?:\?[^#\s]*)?$/.test(value)
    ? value
    : fallback;
}

/** Действие в строке списка кампаний (меню «⋯»). */
export type CampaignRowMenuAction =
  | "results"
  | "copy"
  | "pause"
  | "resume"
  | "cancel"
  | "delete";

/** Статусы, которые отправляют сообщения (пауза и отмена доступны только им). */
export const CAMPAIGN_SENDING_STATUSES = [
  "active",
  "running",
  "scheduled",
  "sending",
];

/**
 * Пункты меню «⋯» строки списка по прототипу и правам: результат (есть
 * отправки или кампания уже запускалась), копия, пауза, возобновление,
 * отмена, удаление — те же условия, что у прежних кнопок строки. Возобновление паузы
 * по бюджету требует ещё и manage (нужно поднять лимит).
 */
export function campaignRowMenu(
  row: { status: string; sent: number; fundingPause?: string },
  rights: { manage: boolean; canLaunch: boolean },
): CampaignRowMenuAction[] {
  const actions: CampaignRowMenuAction[] = [];
  if (row.sent > 0 || row.status !== "draft") actions.push("results");
  if (rights.manage) actions.push("copy");
  if (rights.manage && CAMPAIGN_SENDING_STATUSES.includes(row.status))
    actions.push("pause");
  // Пауза «бюджет исчерпан» снимается только новым лимитом (диалог «Увеличить бюджет»),
  // а лимит меняет право manage: без него возобновить такую кампанию из меню нельзя.
  const budgetPause = row.status === "paused" && row.fundingPause === "budget";
  if (rights.canLaunch && row.status === "paused" && (!budgetPause || rights.manage))
    actions.push("resume");
  // Спека 02 §3.5: отменять можно только то, что отправляет или на паузе.
  if (
    rights.manage &&
    [...CAMPAIGN_SENDING_STATUSES, "paused"].includes(row.status)
  )
    actions.push("cancel");
  if (
    rights.manage &&
    ["draft", "cancelled", "stopped", "paused"].includes(row.status)
  )
    actions.push("delete");
  return actions;
}

/** Главная кнопка строки: черновик — «Продолжить», отправляет/на паузе — «Настройки», иначе «Посмотреть». */
export function campaignOpenLabel(status: string): string {
  if (status === "draft") return "Продолжить";
  return ["paused", ...CAMPAIGN_SENDING_STATUSES].includes(status)
    ? "Настройки"
    : "Посмотреть";
}

/** Поиск по названию кампании в списке (без учёта регистра и ё/е). */
export function matchesCampaignSearch(name: string, query: string): boolean {
  const norm = (value: string) => value.toLowerCase().replace(/ё/g, "е").trim();
  const q = norm(query);
  return !q || norm(name).includes(q);
}
